import React, { useState } from "react";

import {
  CalendarOutlined,
  CloseOutlined,
  InfoCircleFilled,
  WarningFilled,
} from "@ant-design/icons";
import { Button } from "antd";

import { useAccessStatus } from "../hooks/useAccessStatus";
import { useGraceInvoicePayment } from "../hooks/useGraceInvoicePayment";

import { canMakePayment } from "../../../utils/Permissions";
import { formatDaysRemaining, formatGraceEndDate, formatGracePeriodDays } from "../utils";

import "./PaymentPendingBanner.css";

export default function PaymentPendingBanner({
  dismissible = true,
  // "default" | "inset"
  variant = "default",
}) {
  const access = useAccessStatus();
  const { isPaymentBusy, startPayNow, startViewBillingDetails } = useGraceInvoicePayment();
  const allowPayment = canMakePayment();
  const [dismissed, setDismissed] = useState(false);

  if (!access.isGraceActive || (dismissible && dismissed)) {
    return null;
  }

  const daysLeftLabel = formatDaysRemaining(access.grace_days_left);
  const gracePeriodDaysLabel = formatGracePeriodDays(access.grace_days_left);
  const expiresOnLabel = formatGraceEndDate(access.grace_ends_on);

  return (
    <div
      className={`PaymentPendingBanner${variant !== "default" ? ` PaymentPendingBanner--${variant}` : ""}`}
      role="status"
    >
      <WarningFilled className="PaymentPendingBanner__Icon" aria-hidden />

      <div className="PaymentPendingBanner__Copy">
        <div className="PaymentPendingBanner__TitleRow">
          <p className="PaymentPendingBanner__Title">Payment Pending - Result access is limited</p>
          <div className="PaymentPendingBanner__Badges">
            {daysLeftLabel && (
              <span className="PaymentPendingBanner__Badge PaymentPendingBanner__Badge--active">
                <InfoCircleFilled aria-hidden />
                Grace Period Active:
                {" "}
                {daysLeftLabel}
              </span>
            )}
            {expiresOnLabel && (
              <span className="PaymentPendingBanner__Badge">
                <CalendarOutlined aria-hidden />
                Expiring on
                {" "}
                {expiresOnLabel}
              </span>
            )}
          </div>
        </div>
        <p className="PaymentPendingBanner__Description">
          You can continue submitting samples during the
          {gracePeriodDaysLabel ? ` ${gracePeriodDaysLabel}` : ""}
          {" "}
          grace period.
          However, completed results, reports, COAs, analytics, and retest actions remain locked until payment is completed.
        </p>
      </div>

      {(allowPayment || dismissible) && (
        <div className="PaymentPendingBanner__Actions">
          {allowPayment && (
            <>
              <Button
                type="primary"
                size="small"
                className="PaymentPendingBanner__PayNow"
                loading={isPaymentBusy}
                disabled={isPaymentBusy}
                onClick={startPayNow}
              >
                Pay now
              </Button>
              <Button
                type="default"
                size="small"
                className="PaymentPendingBanner__Billing"
                loading={isPaymentBusy}
                disabled={isPaymentBusy}
                onClick={startViewBillingDetails}
              >
                View billing details
              </Button>
            </>
          )}
          {dismissible && (
            <Button
              type="text"
              className="PaymentPendingBanner__Close"
              aria-label="Dismiss payment pending banner"
              icon={<CloseOutlined />}
              disabled={isPaymentBusy}
              onClick={() => setDismissed(true)}
            />
          )}
        </div>
      )}

    </div>
  );
}
